import type { Dispatch, SetStateAction } from 'react';
import { useEffect } from 'react';

/** Maximum age of stored flow data before it is automatically cleared. */
const maxAgeMs = 12 * 60 * 60 * 1000; // 12 hours

/**
 * Auto-clear a `createdAt`-stamped piece of stored flow data after 12 hours.
 *
 * - If `createdAt` is missing (legacy data) or older than 12h, clears immediately.
 * - Otherwise sets a `setTimeout` for the remaining time.
 *
 * Used for the subscription reservation and the ticket change, both of which
 * live in session storage and reference server-side state that is reaped long
 * before the tab is closed.
 */
export function useTempDataExpiry<T extends {
  createdAt?: number;
}>(data: T | null, setData: Dispatch<SetStateAction<T | null>>) {
  useEffect(() => {
    if (!data) return;
    const createdAt = data.createdAt;
    if (!createdAt) {
      // Legacy data without timestamp — clear immediately
      setData(null);
      return;
    }
    const elapsed = Date.now() - createdAt;
    if (elapsed >= maxAgeMs) {
      setData(null);
      return;
    }
    const remaining = maxAgeMs - elapsed;
    const timer = setTimeout(() => {
      setData(null);
    }, remaining);
    return () => clearTimeout(timer);
  }, [data, setData]);
}